// Dependencies
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

// Material UI Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";

// Components
import ListingCard from "components/cards/ListingCard/ListingCard.component";
import VehicleCardSkeleton from "components/cards/VehicleCard/VehicleCard.skeleton";

// Helpers
import { inventoryRoute, vdpRoute } from "routes";
import { fetchVehiclesNearYou } from "reducers/evergreen-reducer";
import useGeolocation from "context/useGeolocation.context";
import usePageWidth from "hooks/usePageWidth.hook";
import { pluralizeString } from "helpers/string-format-helpers";
import times from "lodash/times";

// Styles
import { useStyles } from "./VehiclesNearYou.styles";

const VehiclesNearYou = ({ navRef, genericVehicle }) => {
  const classes = useStyles();
  const { isMobileWidth } = usePageWidth();
  const [location] = useGeolocation();
  const dispatch = useDispatch();

  const listings = useSelector(
    (state) => state.evergreen.vehiclesNearYou,
  ).slice(0, !isMobileWidth ? 8 : 3);
  const vehiclesNearYouResponseIncludesModels = useSelector(
    (state) => state.evergreen.vehiclesNearYouResponseIncludesModels,
  );
  const makeName = genericVehicle?.model?.make?.name;
  const modelName = genericVehicle?.model?.name;
  const makeId = genericVehicle?.model?.make?.id;
  const modelId = genericVehicle?.modelId;

  useEffect(() => {
    if (!location || !makeId) {
      return;
    }
    dispatch(fetchVehiclesNearYou({ location, makeId, modelId }));
  }, [location, makeId]);

  const loaders = times(8, () => (
    <VehicleCardSkeleton key="vehicle-placeholder-1" />
  ));

  const displayNearbyInventory = () => {
    if (listings === undefined || listings.length === 0) {
      return loaders;
    }

    return listings.map((listing) => (
      <ListingCard
        key={listing.id}
        listing={listing}
        dataCy="vehicles-near-you-listing-card"
        href={{
          pathname: vdpRoute,
          query: {
            year: listing.vehicle.year,
            make: listing.vehicle.model.make.name,
            model: listing.vehicle.model.name,
            city: listing.address.city,
            vehicleId: listing.vehicle.id,
          },
        }}
      />
    ));
  };

  const renderTitle = () => {
    if (vehiclesNearYouResponseIncludesModels) {
      if (modelName === modelName.toUpperCase()) {
        return `${makeName} ${modelName}s`;
      }
      return pluralizeString(`${makeName} ${modelName}`);
    }
    return pluralizeString(makeName);
  };

  return (
    <Container
      className={classes.vehiclesNearYouContainer}
      ref={navRef}
      maxWidth="lg"
      data-component="VehiclesNearYou"
    >
      <Typography
        variant="h2"
        className={classes.vehiclesNearYouTitle}
        data-cy="vehicle-name"
      >
        {`${makeName && renderTitle()} near you`}
      </Typography>
      <Grid container spacing={2}>
        {displayNearbyInventory().map((listingCard) => (
          <Grid key={listingCard?.id} item xs={12} sm={6} md={3}>
            {listingCard}
          </Grid>
        ))}
      </Grid>
      <div className={classes.footer}>
        <Link
          href={`${inventoryRoute}/${makeName}/${modelName}`}
          className={classes.linkWrapper}
        >
          <Button
            variant="outlined"
            color="primary"
            className={classes.nearbyVehiclesButton}
          >
            View more
          </Button>
        </Link>
      </div>
    </Container>
  );
};

export default VehiclesNearYou;
